/** Session block that resolves per player to their validated SMART exercise. */
import { localizedExerciseName } from "./localizedCatalogFields";

export function isPersonalizedTypeName(name) {
  const n = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  return n === "personalized" || n === "personnalise";
}

export function isPersonalizedTrainingSlot(exercise) {
  if (!exercise) return false;
  if (exercise.personalized_slot === true) return true;
  const type = exercise.type_ex || exercise.tbl_type_ex || null;
  return (
    isPersonalizedTypeName(type?.type_name_en) ||
    isPersonalizedTypeName(type?.type_name_fr) ||
    isPersonalizedTypeName(type?.type_name)
  );
}

function overrideDetail(exercise, ...viewerIds) {
  const details = exercise?.player_ex_override_details || {};
  for (const id of viewerIds) {
    if (id == null || id === "") continue;
    const hit = details[id] || details[String(id)];
    if (hit) return hit;
  }
  return null;
}

export function overrideForPlayer(exercise, playerId, userId = null) {
  return overrideDetail(exercise, playerId, userId);
}

export function localizedPersonalizedName(detail, lang = "en") {
  if (!detail) return "";
  return localizedExerciseName(detail, lang) || detail.title || "";
}

export function attachPersonalizedToPlayer(player, exercise, lang = "en") {
  if (!player) return player;
  const detail = overrideForPlayer(
    exercise,
    player.playerId ?? player.player_id,
    player.userId ?? player.user_id
  );
  const name = localizedPersonalizedName(detail, lang);
  const slot = isPersonalizedTrainingSlot(exercise);
  return {
    ...player,
    personalizedExerciseName: name || null,
    personalizedMissing: slot && !name,
  };
}

/**
 * Player execution view of a personalized block: the catalogue drill Pingflo
 * assigned on their ★ primary SMART objective (coach-validated).
 * Other blocks are returned unchanged.
 * @param {object} exercise
 * @param {string|number|null} viewerPlayerId
 * @param {string|number|null} [viewerUserId] fallback when overrides are keyed by user_id
 */
export function exerciseForViewerExecution(
  exercise,
  viewerPlayerId,
  viewerUserId = null
) {
  if (!isPersonalizedTrainingSlot(exercise)) return exercise;
  const assigned = overrideDetail(exercise, viewerPlayerId, viewerUserId);
  if (!assigned) {
    return {
      ...exercise,
      personalized_slot: true,
      personalized_pending: true,
    };
  }
  return {
    ...exercise,
    personalized_slot: true,
    personalized_pending: false,
    ex_id: assigned.ex_id || exercise.ex_id,
    ex_name: assigned.ex_name || assigned.ex_name_en || exercise.ex_name,
    ex_name_en: assigned.ex_name_en || assigned.ex_name || exercise.ex_name_en,
    ex_name_fr: assigned.ex_name_fr || assigned.ex_name_en || exercise.ex_name_fr,
    ex_name_de: assigned.ex_name_de || assigned.ex_name_en || exercise.ex_name_de,
    ex_name_es: assigned.ex_name_es || assigned.ex_name_en || exercise.ex_name_es,
    ex_name_nl: assigned.ex_name_nl || assigned.ex_name_en || exercise.ex_name_nl,
    ex_name_pt: assigned.ex_name_pt || assigned.ex_name_en || exercise.ex_name_pt,
    ex_objective_en: assigned.ex_objective_en || null,
    ex_objective_fr: assigned.ex_objective_fr || null,
    ex_objective_de: assigned.ex_objective_de || null,
    ex_objective_es: assigned.ex_objective_es || null,
    ex_instruction_en: assigned.ex_instruction_en || null,
    ex_instruction_fr: assigned.ex_instruction_fr || null,
    ex_process_en: assigned.ex_process_en || null,
    type_ex: assigned.type_ex || exercise.type_ex,
    sub_type_ex: assigned.sub_type_ex || exercise.sub_type_ex,
  };
}
